import { useRef, useEffect } from 'react'
import AssetImageField from '@/components/AssetImageField'
import { normalizeImageKitUrl } from '@lic/media-urls'

export interface SeoData {
  slug: string
  title: string
  text: string
  image_url: string
  seo_title: string
  seo_description: string
  seo_no_index: boolean
  seo_focus_keyword: string
  og_title: string
  og_description: string
  og_image_url: string
}

interface SeoTabProps {
  data: SeoData
  onChange: (field: keyof SeoData, value: string | boolean) => void
}

function charCountClass(len: number, warn: number, max: number) {
  if (len > max) return 'text-red-400'
  if (len > warn) return 'text-yellow-400'
  return 'text-zinc-500'
}

function AutoTextarea({
  value, onChange, placeholder, rows = 1, className = '',
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  rows?: number
  className?: string
}) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  return (
    <textarea
      ref={ref}
      value={value}
      rows={rows}
      onChange={(e) => {
        onChange(e.target.value)
        const el = e.target
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      }}
      placeholder={placeholder}
      className={`w-full bg-zinc-800 text-white rounded px-3 py-2 text-xs border border-zinc-700 focus:outline-none focus:border-zinc-500 resize-none overflow-hidden ${className}`}
    />
  )
}

export default function SeoTab({ data, onChange }: SeoTabProps) {
  const previewTitle = data.seo_title || data.title || 'Article title'
  const previewDesc = data.seo_description || data.text || 'Article description will appear here…'
  const previewSlug = data.slug || 'article-slug'

  const metaTitleLen = data.seo_title.length
  const metaDescLen = data.seo_description.length

  return (
    <div className="space-y-5">
      {/* Indexing toggle */}
      <div>
        <p className="text-xs text-zinc-400 mb-2">Indexing</p>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onChange('seo_no_index', false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              !data.seo_no_index
                ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                : 'bg-zinc-800 text-zinc-500 border border-zinc-700 hover:text-zinc-300'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${!data.seo_no_index ? 'bg-green-400' : 'bg-zinc-600'}`} />
            ON
          </button>
          <button
            type="button"
            onClick={() => onChange('seo_no_index', true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              data.seo_no_index
                ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                : 'bg-zinc-800 text-zinc-500 border border-zinc-700 hover:text-zinc-300'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${data.seo_no_index ? 'bg-red-400' : 'bg-zinc-600'}`} />
            OFF
          </button>
        </div>
        {data.seo_no_index && (
          <p className="mt-1.5 text-[10px] text-red-400/70">Halaman ini tidak akan diindeks oleh Google.</p>
        )}
      </div>

      {/* Focus keyword */}
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Focus Keyword</label>
        <input
          type="text"
          value={data.seo_focus_keyword}
          onChange={(e) => onChange('seo_focus_keyword', e.target.value)}
          placeholder="e.g. idol news jakarta"
          className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-xs border border-zinc-700 focus:outline-none focus:border-zinc-500"
        />
      </div>

      <div className="border-t border-zinc-800" />

      {/* Meta title */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-zinc-400">Meta Title</label>
          <span className={`text-[10px] tabular-nums ${charCountClass(metaTitleLen, 50, 60)}`}>
            {metaTitleLen}/60
          </span>
        </div>
        <AutoTextarea
          value={data.seo_title}
          onChange={(v) => onChange('seo_title', v)}
          placeholder={data.title || 'SEO title…'}
        />
        {!data.seo_title && data.title && (
          <p className="mt-1 text-[10px] text-zinc-600">
            Fallback: <span className="text-zinc-500 italic">"{data.title}"</span>
          </p>
        )}
      </div>

      {/* Meta description */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-zinc-400">Meta Description</label>
          <span className={`text-[10px] tabular-nums ${charCountClass(metaDescLen, 130, 160)}`}>
            {metaDescLen}/160
          </span>
        </div>
        <AutoTextarea
          value={data.seo_description}
          onChange={(v) => onChange('seo_description', v)}
          placeholder={data.text || 'SEO description…'}
          rows={3}
        />
        {!data.seo_description && data.text && (
          <p className="mt-1 text-[10px] text-zinc-600">
            Fallback: <span className="text-zinc-500 italic">"{data.text}"</span>
          </p>
        )}
      </div>

      {/* Google Preview */}
      <div>
        <p className="text-xs text-zinc-400 mb-2">Google Preview</p>
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 space-y-0.5">
          <p className="text-[10px] text-green-500/80 truncate">
            licplatform.com › news › {previewSlug}
          </p>
          <p className="text-xs text-blue-400 font-medium leading-snug line-clamp-2">
            {previewTitle}
          </p>
          <p className="text-[11px] text-zinc-400 leading-relaxed line-clamp-2">
            {previewDesc}
          </p>
        </div>
      </div>

      <div className="border-t border-zinc-800" />

      {/* Open Graph */}
      <div className="space-y-4">
        <p className="text-xs text-zinc-400 font-medium">Open Graph</p>

        <div>
          <label className="block text-xs text-zinc-500 mb-1">OG Title</label>
          <input
            type="text"
            value={data.og_title}
            onChange={(e) => onChange('og_title', e.target.value)}
            placeholder={data.seo_title || data.title || 'OG title'}
            className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-xs border border-zinc-700 focus:outline-none focus:border-zinc-500"
          />
        </div>

        <div>
          <label className="block text-xs text-zinc-500 mb-1">OG Description</label>
          <AutoTextarea
            value={data.og_description}
            onChange={(v) => onChange('og_description', v)}
            placeholder={data.seo_description || data.text || 'OG description'}
            rows={2}
          />
        </div>

        <div>
          <label className="block text-xs text-zinc-500 mb-1">OG Image</label>
          <AssetImageField
            value={data.og_image_url}
            onChange={(url) => onChange('og_image_url', url)}
            feature="news"
          />
          {!data.og_image_url && data.image_url && (
            <div className="mt-2 flex items-center gap-2">
              <img
                src={normalizeImageKitUrl(data.image_url)}
                alt="Cover fallback"
                className="h-10 w-16 rounded object-cover border border-zinc-700 opacity-50"
              />
              <p className="text-[10px] text-zinc-600">Fallback: <span className="text-zinc-500">cover image</span></p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
